import type { Ticket } from '../models/RestModels';
import type { Printer } from '../models/RestModels';
import type { Store } from '../models/RestModels';
import type { RestService } from '../services/rest.service';
import type { PrinterService } from '../services/printer.service';

// ---------- receipt data ----------

export interface PaymentReceiptStore {
	name: string;
	business_name?: string | null;
	rfc?: string | null;
	address?: string | null;
	city?: string | null;
	state?: string | null;
	zipcode?: string | null;
	phone?: string | null;
}

export interface AbonoRow {
	date: string | Date;
	type_code: string;
	amount: number;
	currency_id: string;
	balance: number;
}

export interface OrderRow {
	order_id: number;
	order_total: number;
	prev_balance: number;
	final_balance: number;
	currency_id: string;
}

export interface PaymentReceiptBase {
	title: string;
	store: PaymentReceiptStore | null;
	client_name: string;
	attendant_name: string;
	base_currency_id: string;
	received_amount: number;
	change_amount: number;
	total_abonado: number;
	pendiente: number;
}

export interface SingleOrderReceiptData extends PaymentReceiptBase {
	kind: 'SINGLE_ORDER';
	order_id: number | null;
	folio: number | string | null;
	subtotal: number;
	discount: number;
	total: number;
	abonos: AbonoRow[];
}

export interface MultiOrderReceiptData extends PaymentReceiptBase {
	kind: 'MULTI_ORDER';
	payment_id: number | null;
	payment_date: string | Date;
	payment_codes: string;
	orders: OrderRow[];
}

export type PaymentReceiptTicketData = SingleOrderReceiptData | MultiOrderReceiptData;

// ---------- payment type codes ----------

// Browser receipts (view-payment, print-payment-receipt) map CREDIT_CARD to
// debito and DEBIT_CARD to credito; mirrored here so USB matches paper.
const TYPE_CODES: Record<string, string> = {
	'CASH': 'EF',
	'CREDIT_CARD': 'TD',
	'DEBIT_CARD': 'TC',
	'CHECK': 'CH',
	'TRANSFER': 'TR',
};

const TYPE_NAMES: Record<string, string> = {
	'EF': 'EFECTIVO',
	'DL': 'DOLARES',
	'TD': 'T.DEBITO',
	'TC': 'T.CREDITO',
	'CH': 'CHEQUE',
	'TR': 'TRANSFERENCIA',
};

export function paymentTypeCode(transaction_type: string, currency_id: string): string {
	if (transaction_type === 'CASH') {
		return currency_id === 'USD' ? 'DL' : 'EF';
	}
	return TYPE_CODES[transaction_type]
		|| (transaction_type || '').slice(0, 2).toUpperCase()
		|| '??';
}

export function paymentTypeName(code: string): string {
	return TYPE_NAMES[code] || code;
}

// Minimal structural shape so PaymentInfo[] fits with no casts and plain
// literals work in tests.
export interface HistoryPaymentLike {
	payment: { id?: number | null };
	movements?: Array<{
		bank_movement: { transaction_type: string };
		bank_movement_orders?: Array<{
			order_id?: number | null;
			amount?: number | null;
			currency_amount?: number | null;
			currency_id: string;
			created?: Date;
		}> | null;
	}> | null;
}

export interface AbonoHistory {
	abonos: AbonoRow[];
	total_abonado: number;
}

// Mirrors the print-payment-receipt accumulation: payments in id order,
// only bmos for this order, running sum over base amount, balance against
// the net order total. Display amount stays in payment currency.
export function buildAbonoHistory(
	payments: HistoryPaymentLike[],
	order_id: number | null,
	net_total: number,
	max_payment_id?: number | null
): AbonoHistory {
	const abonos: AbonoRow[] = [];
	let sum = 0;
	for (const pi of payments) {
		if (max_payment_id != null && pi.payment.id != null && pi.payment.id > max_payment_id) {
			continue;
		}
		for (const movement of (pi.movements || [])) {
			for (const bmo of (movement.bank_movement_orders || [])) {
				if (bmo.order_id !== order_id) {
					continue;
				}
				sum += bmo.amount || 0;
				abonos.push({
					date: bmo.created || '',
					type_code: paymentTypeCode(movement.bank_movement.transaction_type, bmo.currency_id),
					amount: bmo.currency_amount || 0,
					currency_id: bmo.currency_id,
					balance: net_total - sum
				});
			}
		}
	}
	return { abonos, total_abonado: sum };
}

// ---------- formatting helpers (bare numbers, no currency sign) ----------

export function formatBareAmount(amount: number): string {
	const v = (typeof amount === 'number' && !isNaN(amount)) ? amount : 0;
	return v.toFixed(2);
}

// Totals rows use thousands separators (30,000.00); tables stay bare.
export function formatGroupedAmount(amount: number): string {
	const v = (typeof amount === 'number' && !isNaN(amount)) ? amount : 0;
	return v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function clampDisplayBalance(amount: number): number {
	const v = (typeof amount === 'number' && !isNaN(amount)) ? amount : 0;
	return Math.max(0, v);
}

// DD/MM/YY HH:MM (24h). String dates are read as calendar fields (no
// timezone shift); Date instances use local getters.
export function formatTicketDateTime(value: string | Date): string {
	const pad = (n: number) => (n < 10 ? '0' : '') + n;
	if (typeof value === 'string') {
		const m = value.match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
		if (m) {
			return `${m[3]}/${m[2]}/${m[1].slice(2)} ${m[4]}:${m[5]}`;
		}
		return '';
	}
	if (value instanceof Date && !isNaN(value.getTime())) {
		return `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${String(value.getFullYear()).slice(2)} ${pad(value.getHours())}:${pad(value.getMinutes())}`;
	}
	return '';
}

// Scale design widths to the available columns. Margins take one column
// each; every column keeps at least one. The total never exceeds `total`.
export function scaleColumnWidths(design: number[], total: number): number[] {
	const margins = design.length - 1;
	const avail = Math.max(design.length, total - margins);
	const sum = design.reduce((a, b) => a + b, 0);
	const widths: number[] = [];
	let used = 0;
	for (let i = 0; i < design.length; i++) {
		if (i === design.length - 1) {
			widths.push(Math.max(1, avail - used));
		} else {
			const w = Math.max(1, Math.floor(avail * design[i] / sum));
			widths.push(w);
			used += w;
		}
	}
	return widths;
}

export class PaymentReceiptTicketBuilder {

	ticket_a_columns: number = 42;
	ticket_b_columns: number = 56;
	font_row: string = 'b';

	private data: PaymentReceiptTicketData;
	private ticket: Ticket | null;

	constructor(
		data: PaymentReceiptTicketData,
		ticket_a_columns: number = 42,
		ticket_b_columns: number = 56,
		ticket: Ticket | null = null
	) {
		this.data = data;
		this.ticket_a_columns = ticket_a_columns;
		this.ticket_b_columns = ticket_b_columns;
		this.ticket = ticket;
	}

	private getMaxColumns(font_name: 'a' | 'b' = 'b'): number {
		return font_name === 'a' ? this.ticket_a_columns : this.ticket_b_columns;
	}

	private escapeXml(str: string): string {
		if (!str) return '';
		return str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&apos;');
	}

	private sanitizeXml(xml: string): string {
		return xml.replace(/<[^<>]*>/g, (tag) =>
			tag.replace(/(\w+)=([^\s"'<>]+)(?=[\s>/])/g, '$1="$2"')
		);
	}

	// Raw custom XML passes through; plain text centers; malformed XML drops.
	private customXmlOrText(value: string): string {
		if (!value) return '';
		if (value.includes('<') && value.includes('</')) {
			return this.sanitizeXml(value) + '\n';
		}
		if (value.includes('<')) {
			console.warn('Skipping malformed ticket XML:', value);
			return '';
		}
		return `<Text align="center">${this.escapeXml(value)}</Text>\n<Feed lines="1"/>\n`;
	}

	private line(text: string, align: string = 'left', font: string = 'b', bold: boolean = false): string {
		const b = bold ? ' bold="true"' : '';
		return `<Text align="${align}" font="${font}"${b}>${this.escapeXml(text)}</Text>\n<Feed lines="1"/>\n`;
	}

	public buildXml(): string {
		let xml = '<EscPos>\n';
		xml += '<Init/>\n';
		xml += this.buildStoreHeader();
		xml += this.buildTitle();
		xml += this.buildInfoLines();
		xml += this.buildKeyLines();
		xml += this.data.kind === 'SINGLE_ORDER' ? this.buildSingleTable() : this.buildMultiTable();
		xml += this.buildTotals();
		xml += this.customXmlOrText(this.ticket?.footer || '');
		xml += '<Feed lines="2"/>\n';
		xml += '<Cut mode="partial"/>\n';
		xml += '</EscPos>';
		return xml;
	}

	private buildStoreHeader(): string {
		if (this.ticket?.header) {
			return this.customXmlOrText(this.ticket.header);
		}
		const store = this.data.store;
		if (!store?.name) return '';
		let xml = '';
		xml += this.line(store.name, 'center', 'b', true);
		if (store.business_name) xml += this.line(store.business_name, 'center');
		if (store.rfc) xml += this.line(store.rfc, 'center');
		const parts = [store.address, store.city, store.state, store.zipcode].filter(p => !!p);
		if (parts.length) xml += this.line(parts.join(' '), 'center');
		if (store.phone) xml += this.line(`TEL: ${store.phone}`, 'center');
		return xml;
	}

	private buildTitle(): string {
		return this.line(this.data.title, 'center', 'a', true);
	}

	private buildInfoLines(): string {
		let xml = '';
		const d = this.data;
		if (d.kind === 'MULTI_ORDER') {
			const pago = d.payment_id ? `PAGO #${d.payment_id}` : 'PAGO S/N';
			const fecha = formatTicketDateTime(d.payment_date);
			xml += this.line(`${pago} ${fecha} ${d.payment_codes}`.trim(), 'left');
		} else {
			const orden = d.order_id ? `ORDEN #${d.order_id}` : 'ORDEN S/N';
			const folio = d.folio ? ` FOLIO ${d.folio}` : '';
			xml += this.line(`${orden}${folio}`, 'left');
			xml += this.line(`SUB:${formatBareAmount(d.subtotal)} DESC:${formatBareAmount(d.discount)} TOT:${formatBareAmount(d.total)}`, 'left');
		}
		if (d.client_name) xml += this.line(`CLIENTE: ${d.client_name}`, 'left');
		if (d.attendant_name) xml += this.line(`ATENDIO: ${d.attendant_name}`, 'left');
		if (d.base_currency_id) xml += this.line(`MONTOS EN ${d.base_currency_id}`, 'left');
		return xml;
	}

	private usedTypeCodes(): string[] {
		const codes: string[] = [];
		const push = (c: string) => { if (c && !codes.includes(c)) codes.push(c); };
		if (this.data.kind === 'SINGLE_ORDER') {
			this.data.abonos.forEach(a => push(a.type_code));
		} else {
			(this.data.payment_codes || '').split('/').forEach(push);
		}
		return codes;
	}

	// `EF=EFECTIVO TR=TRANSFERENCIA`, packed to the line width.
	private buildKeyLines(): string {
		const maxCols = this.getMaxColumns('b');
		const keys = this.usedTypeCodes().map(c => `${c}=${paymentTypeName(c)}`);
		let xml = '';
		let current = '';
		const flush = () => {
			if (current) xml += this.line(current, 'left');
			current = '';
		};
		for (const key of keys) {
			const next = current ? `${current} ${key}` : key;
			if (next.length > maxCols && current) {
				flush();
				current = key;
			} else {
				current = next;
			}
		}
		flush();
		return xml;
	}

	private tableColumns(widths: number[], aligns: string[]): string {
		let xml = '<Columns>\n';
		for (let i = 0; i < widths.length; i++) {
			const margin = i < widths.length - 1 ? ' marginRight="1"' : '';
			xml += `<Column width="${widths[i]}" align="${aligns[i]}"${margin}></Column>\n`;
		}
		xml += '</Columns>\n';
		return xml;
	}

	private headerRow(labels: string[], aligns: string[]): string {
		let xml = '<Row>\n';
		for (let i = 0; i < labels.length; i++) {
			xml += `<Cell><Text align="${aligns[i]}" bold="true">${labels[i]}</Text></Cell>\n`;
		}
		xml += '</Row>\n';
		return xml;
	}

	private ruleRow(count: number): string {
		let xml = '<Row>\n';
		for (let i = 0; i < count; i++) {
			xml += '<Cell><Rule></Rule></Cell>\n';
		}
		xml += '</Row>\n';
		return xml;
	}

	private buildSingleTable(): string {
		const data = this.data as SingleOrderReceiptData;
		const aligns = ['left', 'left', 'right', 'right'];
		const widths = scaleColumnWidths([15, 8, 15, 15], this.getMaxColumns('b'));
		let xml = `<Table font="${this.font_row}">\n`;
		xml += this.tableColumns(widths, aligns);
		xml += '<Rows>\n';
		xml += this.headerRow(['FECHA', 'TIPO', 'ABONO', 'BALANCE'], aligns);
		xml += this.ruleRow(4);
		for (const abono of data.abonos) {
			const amount = abono.currency_id && abono.currency_id !== data.base_currency_id
				? `${formatBareAmount(abono.amount)} ${abono.currency_id}`
				: formatBareAmount(abono.amount);
			const balance = formatBareAmount(clampDisplayBalance(abono.balance));
			xml += '<Row>\n';
			xml += `<Cell><Text align="left">${formatTicketDateTime(abono.date)}</Text></Cell>\n`;
			xml += `<Cell><Text align="left">${this.escapeXml(abono.type_code)}</Text></Cell>\n`;
			xml += `<Cell><Text align="right">${amount}</Text></Cell>\n`;
			xml += `<Cell><Text align="right">${balance}</Text></Cell>\n`;
			xml += '</Row>\n';
		}
		xml += this.ruleRow(4);
		xml += '</Rows>\n';
		xml += '</Table>\n';
		return xml;
	}

	private buildMultiTable(): string {
		const data = this.data as MultiOrderReceiptData;
		const aligns = ['left', 'right', 'right', 'right'];
		const widths = scaleColumnWidths([8, 14, 15, 15], this.getMaxColumns('b'));
		let xml = `<Table font="${this.font_row}">\n`;
		xml += this.tableColumns(widths, aligns);
		xml += '<Rows>\n';
		xml += this.headerRow(['ORDEN', 'TOTAL', 'SALDO ANT', 'SALDO'], aligns);
		xml += this.ruleRow(4);
		for (const order of data.orders) {
			const suffix = order.currency_id && order.currency_id !== data.base_currency_id ? ` ${order.currency_id}` : '';
			xml += '<Row>\n';
			xml += `<Cell><Text align="left">#${order.order_id}</Text></Cell>\n`;
			xml += `<Cell><Text align="right">${formatBareAmount(order.order_total)}${suffix}</Text></Cell>\n`;
			xml += `<Cell><Text align="right">${formatBareAmount(clampDisplayBalance(order.prev_balance))}${suffix}</Text></Cell>\n`;
			xml += `<Cell><Text align="right">${formatBareAmount(clampDisplayBalance(order.final_balance))}${suffix}</Text></Cell>\n`;
			xml += '</Row>\n';
		}
		xml += this.ruleRow(4);
		xml += '</Rows>\n';
		xml += '</Table>\n';
		return xml;
	}

	private totalsRow(label: string, value: string, maxCols: number, bold: boolean = false): string {
		const dots = '.'.repeat(Math.max(0, maxCols - label.length - value.length - 2));
		const b = bold ? ' bold="true"' : '';
		return `<Text align="left" font="a"${b}>${this.escapeXml(label)} ${dots} ${this.escapeXml(value)}</Text>\n<Feed lines="1"/>\n`;
	}

	private buildTotals(): string {
		const maxCols = this.getMaxColumns('a');
		const d = this.data;
		let xml = '<Feed lines="1"/>\n';
		xml += this.totalsRow('Total Abonado', formatGroupedAmount(d.total_abonado), maxCols);
		xml += this.totalsRow('Total Recibido', formatGroupedAmount(d.received_amount), maxCols, true);
		xml += this.totalsRow('Cambio', formatGroupedAmount(d.change_amount), maxCols, true);
		xml += this.totalsRow('Pendiente', formatGroupedAmount(clampDisplayBalance(d.pendiente)), maxCols);
		return xml;
	}
}

// ---------- sample fixtures (paper tests) ----------

export function sampleSingleOrderReceiptData(): SingleOrderReceiptData {
	return {
		kind: 'SINGLE_ORDER',
		title: 'RECIBO DE ABONOS',
		store: {
			name: 'TIENDA EJEMPLO',
			business_name: 'EJEMPLO SA DE CV',
			rfc: 'XAXX010101000',
			address: 'CALLE 123',
			city: 'CIUDAD',
			state: 'ESTADO',
			zipcode: '00000',
			phone: '5551234567'
		},
		client_name: 'JUAN PEREZ',
		attendant_name: 'MARIA LOPEZ',
		base_currency_id: 'MXN',
		received_amount: 900,
		change_amount: 0,
		total_abonado: 1400,
		pendiente: 0,
		order_id: 1234,
		folio: 567,
		subtotal: 1500,
		discount: 100,
		total: 1400,
		abonos: [
			{ date: '2026-05-12 10:30:00', type_code: 'EF', amount: 500, currency_id: 'MXN', balance: 900 },
			{ date: '2026-06-12 11:00:00', type_code: 'TR', amount: 900, currency_id: 'MXN', balance: 0 }
		]
	};
}

export function sampleMultiOrderReceiptData(): MultiOrderReceiptData {
	return {
		kind: 'MULTI_ORDER',
		title: 'RECIBO DE PAGO',
		store: {
			name: 'TIENDA EJEMPLO',
			business_name: 'EJEMPLO SA DE CV',
			rfc: 'XAXX010101000',
			address: 'CALLE 123',
			city: 'CIUDAD',
			state: 'ESTADO',
			zipcode: '00000',
			phone: '5551234567'
		},
		client_name: 'JUAN PEREZ',
		attendant_name: 'MARIA LOPEZ',
		base_currency_id: 'MXN',
		received_amount: 700,
		change_amount: 0,
		total_abonado: 700,
		pendiente: 300,
		payment_id: 290,
		payment_date: '2026-06-12 11:00:00',
		payment_codes: 'EF',
		orders: [
			{ order_id: 33333, order_total: 1500, prev_balance: 800, final_balance: 300, currency_id: 'MXN' },
			{ order_id: 33334, order_total: 200, prev_balance: 200, final_balance: 0, currency_id: 'MXN' }
		]
	};
}

// ---------- USB print glue (called with app services, no DI here) ----------

export function storeSnapshot(store: Store | null | undefined): PaymentReceiptStore | null {
	if (!store) return null;
	return {
		name: store.name || '',
		business_name: store.business_name || null,
		rfc: store.rfc || null,
		address: store.address || null,
		city: store.city || null,
		state: store.state || null,
		zipcode: store.zipcode || null,
		phone: store.phone || null
	};
}

// Ticket resolution mirrors the POS sale-ticket flow: synced ticket first,
// then the printer's linked ticket, then the first ticket on file.
export async function resolveTicketForUsbPrint(
	rest: RestService,
	printerService: PrinterService,
	printer: Printer
): Promise<Ticket | null> {
	let ticket: Ticket | null = printerService.getTicket() || rest.current_ticket || null;

	if (!ticket && printer?.ticket_id) {
		try {
			ticket = await rest.ticket.get(printer.ticket_id).toPromise();
		} catch (_e) {
			ticket = null;
		}
	}

	if (!ticket) {
		try {
			const response = await rest.ticket.search({ limit: 1, sort_order: ['id_ASC'] }).toPromise();
			ticket = response.data.length > 0 ? response.data[0] : null;
		} catch (_e) {
			ticket = null;
		}
	}

	return ticket;
}

// Loads the store printers into the service when empty so getPosPrinter can
// resolve. Never assigns preferences; the user picks the POS printer.
export async function ensurePosPrinterLoaded(
	rest: RestService,
	printerService: PrinterService,
	store_id: number | null | undefined
): Promise<void> {
	if (printerService.available_printers.length > 0 || !store_id) {
		return;
	}
	const response = await rest.printer.search({
		limit: 9999,
		eq: { store_id },
		sort_order: ['name_ASC']
	}).toPromise();
	printerService.available_printers = response.data || [];
}
